import { MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChatBubbleProps {
  isOpen: boolean;
  onClick: () => void;
  onDismiss?: () => void;
  hasUnreadGreeting?: boolean;
  hasOpenedChat?: boolean;
  bottomOffset?: number;
}

const ChatBubble = ({ 
  isOpen, 
  onClick, 
  onDismiss,
  hasUnreadGreeting = false,
  hasOpenedChat = false,
  bottomOffset = 24
}: ChatBubbleProps) => {
  const isMobile = useIsMobile();
  // Show pulsing indicator only when there's an unread greeting that hasn't been seen
  const showPulsingIndicator = !isOpen && hasUnreadGreeting && !hasOpenedChat;

  return (
    <div 
      className="fixed right-6 z-50 transition-all duration-200"
      style={{ bottom: `${bottomOffset}px` }}
    >
      {/* Mobile dismiss button - only show when chat is closed */}
      {isMobile && !isOpen && onDismiss && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-muted border border-border flex items-center justify-center shadow-md hover:bg-muted/80 transition-colors"
          aria-label="Hide chat"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      )}

      {/* Pulse ring animation for attention */}
      {showPulsingIndicator && (
        <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
      )}
      
      <Button
        onClick={onClick}
        className={cn(
          "relative shadow-2xl transition-all duration-300 hover:scale-105",
          "bg-primary hover:bg-primary/90 text-primary-foreground",
          "border-2 border-white/80 ring-1 ring-white/20",
          // Pill shape on desktop, circle on mobile
          isOpen 
            ? "h-14 w-14 rounded-full" 
            : "h-14 px-5 rounded-full md:rounded-full md:px-6"
        )}
        aria-label={isOpen ? "Close chat" : "Chat with us"}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <div className="flex items-center gap-2">
            <MessageCircle className="h-6 w-6" />
            <span className="hidden md:inline text-sm font-medium whitespace-nowrap">
              Chat with us
            </span>
          </div>
        )}
        
        {/* Pulsing indicator for unread greeting only */}
        {showPulsingIndicator && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-primary animate-pulse" />
        )}
      </Button>
    </div>
  );
};

export default ChatBubble;
